import { DateTime } from 'luxon';

import { coreActions } from 'app-components/flux/core-actions';
import { getLogger, tracker } from 'app-components/utils/core';
import { getCachedState } from 'app-components/utils/persist';

import { appSetOnlineStatus } from 'data/app/actions';
import { createAppStore, type RootStore } from 'store';
import { createPersistConfig } from 'store/createPersistConfig';
import { persistedAuthSession } from 'store/persistedAuthSession';
/////////////////////////////////////////
import { isDevSsoAttempt, reuseDevSsoStore } from 'utils/devSso/capture';
/////////

// The store can't be replaced in place: its sagas run on core's shared saga middleware and can't be
// stopped. So an edit to anything the store imports reloads the page instead of hot-applying.
/////////////////////////////////////////////
import.meta.hot?.accept(() => window.location.reload());
/////////

const log = getLogger('startup/initializeAppStore');

async function readCachedState(): Promise<object> {
  const { qcsId, datasetId } = persistedAuthSession;
  // Nothing is cached without a signed-in dataset, and the auth widget's iframe never restores the cache.
  if (!qcsId || !datasetId || window !== window.top) return {};
  try {
    return await getCachedState(createPersistConfig(qcsId, datasetId));
  } catch (error) {
    log.error('Failed to get persisted state: ', error);
    return {};
  }
}

function createOrReuseAppStore(cachedState: object): RootStore {
///////////////////////////////////////////
  // A token login selects an account, so it never restores another account's cache.
  if (isDevSsoAttempt()) return reuseDevSsoStore(() => createAppStore({}));
///////////
  return createAppStore(cachedState);
}

async function loadAppStore(): Promise<RootStore> {
  const start = DateTime.now();
  tracker.timeEvent(tracker.events.persistLoadComplete);
  tracker.track(tracker.events.persistLoadStart);

  const store = createOrReuseAppStore(await readCachedState());
  // Seed before the first render so an offline cold start can show the offline page.
  store.dispatch(appSetOnlineStatus(navigator.onLine));

  const duration = start.diffNow();
  log.log(`load from persist complete in ${Math.abs(duration.as('milliseconds'))}ms...`);
  tracker.track(tracker.events.persistLoadComplete, { Duration: Math.abs(duration.as('seconds')) });
  store.dispatch(coreActions.persistDataLoadComplete());

  return store;
}

let appStore: Promise<RootStore> | undefined;

/** Starts the app store once per page and returns it; later calls and remounts reuse the same promise. */
export function initializeAppStore(): Promise<RootStore> {
  appStore ??= loadAppStore();
  return appStore;
}
